import * as React from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Card } from '@ps/ui-kit/components/card';

// Staff-only. Renders EXACTLY the actions the server returned (availableActions). The UI never decides what is
// allowed: a button shown but refused by the API is a defect (handover 03 A-02). Prototype styling: the main next step
// filled, the others outlined, refusals and reports outlined in red; an action can be a form shown in place
// (`inline`, e.g. the alert's close form) instead of a button.
export interface ActionRailProps {
  state: string;
  role: string;
  actions: string[];
  labels: Record<string, string>;
  onAction: (a: string) => void;
  busy?: string | null;
  title?: string;
  primary?: string | undefined;         // the main next step, drawn filled
  inline?: Record<string, React.ReactNode>;
  explanation?: string | null;          // why Approve is not offered
  done?: string[];                      // actions whose work is finished: shown with a tick, still clickable
  progress?: Record<string, { done: number; total: number }>;   // work in progress: a bar along the button's bottom edge
  children?: React.ReactNode;           // SLA box etc.
}

const DANGER = new Set(['REJECT', 'INTERNAL_REPORT']);

export function ActionRail({ state, role, actions, labels, onAction, busy, title = 'Available actions', primary, inline = {}, explanation, done = [], progress = {}, children }: ActionRailProps) {
  return (
    <Card role="complementary" aria-label="Actions" className="p-4">
      <h2 className="font-display font-bold text-ink">{title}</h2>
      <p className="text-xs text-ink-3">State {state.replaceAll('_', ' ')} · you are {role}</p>
      <div className="mt-3 flex flex-col gap-2">
        {actions.length === 0 && <p className="text-sm text-ink-3">No actions for you at this stage.</p>}
        {actions.map(a => {
          if (inline[a]) return <React.Fragment key={a}>{inline[a]}</React.Fragment>;
          const p = done.includes(a) ? undefined : progress[a];
          const look = a === primary || a === 'APPROVE' ? 'bg-tick-ink text-white hover:bg-moss'
            : DANGER.has(a) ? 'border border-bad text-bad hover:bg-bad-bg'
            : done.includes(a) ? 'border border-tick/40 bg-mint text-tick-ink hover:bg-mint-2' : 'border border-tick text-tick-ink hover:bg-mint-2';
          return (
            <button key={a} onClick={() => onAction(a)} disabled={!!busy}
              aria-label={p ? `${labels[a] ?? a}: ${p.done} of ${p.total} done` : undefined}
              className={`relative flex items-center justify-center gap-2 overflow-hidden rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-60 ${look}`}>
              {busy === a ? <Loader2 className="size-4 animate-spin" aria-hidden /> : done.includes(a) && <CheckCircle2 className="size-4" aria-hidden />}{labels[a] ?? a}
              {p && (
                <span aria-hidden className="absolute inset-x-0 bottom-0 h-1 bg-mint">
                  <span className="block h-full bg-tick" style={{ width: `${Math.round((p.done / Math.max(p.total, 1)) * 100)}%` }} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      {explanation && <p className="mt-3 border-t border-line pt-3 text-xs text-ink-2">{explanation}</p>}
      {children}
    </Card>
  );
}
